import { Link } from "@inertiajs/react"
import { useCallback, useEffect, useRef, useState } from "react"

import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox"

interface Option {
  id: number
  name: string
}

interface ReferralResult {
  id: string
  type: string
  customer_id?: number
  vendor_id?: number
  label: string
}

interface StatusOption {
  value: number
  label: string
}

interface EmployeeCheckbox {
  id: number
  name: string
  checked: boolean
}

interface NotifyEmployee {
  id: number
  name: string
  forced: boolean
  default_checked: boolean
}

interface CaseFormData {
  case_number: string | null
  file_number: string
  case_reference: string
  hpi_name: string
  qb_name: string
  report_name: string
  directory: string
  customer_id: number | null
  customer_user_id: number | null
  customer_location_id: number | null
  investigation_type_id: number | null
  manager_id: number | null
  referral_case_id: number | null
  location_city: string
  location_state: string
  open_date: string | null
  closed_date: string | null
  accepted_date: string | null
  paid_date: string | null
  budget: number | null
  retainer: number | null
  results: number | null
  results_notes: string
  notes: string
  objectives: string
  internal_notes: string
  has_attorney: boolean
  attorney_name: string
  attorney_phone: string
  attorney_law_firm: string
  attorney_email: string
  prepared_for: string
  matter: string
  accepted: number | null
  accepted_text: string
  after_hours: boolean
  is_public: boolean
  hpi_company: number | null
  status: number
  intake_by_id: number | null
  other_type: string
  insurance_days: number | null
  insurance_budget_notes: string
  referral_customer_id: number | null
  referral_customer_user_id: number | null
}

export interface EmployeeAccessData {
  allow_employee_ids: number[]
  deny_employee_ids: number[]
  notify_user_ids: number[]
}

interface Props {
  data: CaseFormData
  setData: (
    key: keyof CaseFormData,
    value: string | number | boolean | null,
  ) => void
  errors?: Record<string, string[]>
  processing: boolean
  onSubmit: (e: React.FormEvent, employeeData: EmployeeAccessData) => void
  submitLabel: string
  customer_display_name?: string
  investigation_types: Option[]
  managers: Option[]
  users: Option[]
  status_options: StatusOption[]
  referral_display_name?: string
  isEdit?: boolean
  allow_employees?: EmployeeCheckbox[]
  deny_employees?: EmployeeCheckbox[]
  notify_employees?: NotifyEmployee[]
}

export default function CaseForm({
  data,
  setData,
  errors,
  processing,
  onSubmit,
  submitLabel,
  customer_display_name = "",
  investigation_types,
  managers,
  users,
  status_options,
  referral_display_name = "",
  isEdit = false,
  allow_employees = [],
  deny_employees = [],
  notify_employees = [],
}: Props) {
  const selectedTypeName = investigation_types.find(
    (it) => it.id === data.investigation_type_id,
  )?.name

  const [customerContacts, setCustomerContacts] = useState<Option[]>([])
  const [customerLocations, setCustomerLocations] = useState<Option[]>([])
  const [intakeHours, setIntakeHours] = useState(0)
  const [intakeMinutes, setIntakeMinutes] = useState(0)
  const [referralName, setReferralName] = useState(referral_display_name)

  // Async customer search
  const [customerResults, setCustomerResults] = useState<Option[]>([])
  const customerDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const fetchCustomers = useCallback((term: string) => {
    if (term.length < 2) {
      setCustomerResults([])
      return
    }
    fetch(`/customers/search_for_select?q=${encodeURIComponent(term)}`, {
      headers: { Accept: "application/json" },
    })
      .then((r) => r.json())
      .then(setCustomerResults)
      .catch(() => setCustomerResults([]))
  }, [])

  const handleCustomerInputChange = useCallback(
    (value: string) => {
      if (customerDebounceRef.current) clearTimeout(customerDebounceRef.current)
      if (value.length < 2) {
        setCustomerResults([])
        return
      }
      customerDebounceRef.current = setTimeout(() => fetchCustomers(value), 300)
    },
    [fetchCustomers],
  )

  // Async referral search
  const [referralResults, setReferralResults] = useState<ReferralResult[]>([])
  const referralDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const fetchReferrals = useCallback((term: string) => {
    if (term.length < 3) {
      setReferralResults([])
      return
    }
    fetch(`/customers/referral_search?q=${encodeURIComponent(term)}`, {
      headers: { Accept: "application/json" },
    })
      .then((r) => r.json())
      .then(setReferralResults)
      .catch(() => setReferralResults([]))
  }, [])

  const handleReferralInputChange = useCallback(
    (value: string) => {
      if (referralDebounceRef.current) clearTimeout(referralDebounceRef.current)
      if (value.length < 3) {
        setReferralResults([])
        return
      }
      referralDebounceRef.current = setTimeout(() => fetchReferrals(value), 300)
    },
    [fetchReferrals],
  )

  const [allowChecked, setAllowChecked] = useState<Record<number, boolean>>(
    () => Object.fromEntries(allow_employees.map((e) => [e.id, e.checked])),
  )
  const [denyChecked, setDenyChecked] = useState<Record<number, boolean>>(() =>
    Object.fromEntries(deny_employees.map((e) => [e.id, e.checked])),
  )

  const [notifyChecked, setNotifyChecked] = useState<Record<number, boolean>>(
    () =>
      Object.fromEntries(
        notify_employees.map((e) => [e.id, e.forced || e.default_checked]),
      ),
  )

  // Fetching customer-dependent data on customer_id change requires setting state from async responses
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!data.customer_id) {
      setCustomerContacts([])
      setCustomerLocations([])
      return
    }

    fetch(`/customers/${data.customer_id}/contacts_for_select`, {
      headers: { Accept: "application/json" },
    })
      .then((r) => r.json())
      .then(setCustomerContacts)
      .catch(() => setCustomerContacts([]))

    fetch(`/customers/${data.customer_id}/locations_for_select`, {
      headers: { Accept: "application/json" },
    })
      .then((r) => r.json())
      .then(setCustomerLocations)
      .catch(() => setCustomerLocations([]))
  }, [data.customer_id])
  /* eslint-enable react-hooks/set-state-in-effect */

  return (
    <form
      onSubmit={(e) => {
        const employeeData: EmployeeAccessData = {
          allow_employee_ids: Object.entries(allowChecked)
            .filter(([, v]) => v)
            .map(([k]) => parseInt(k, 10)),
          deny_employee_ids: Object.entries(denyChecked)
            .filter(([, v]) => v)
            .map(([k]) => parseInt(k, 10)),
          notify_user_ids: [
            ...notify_employees
              .filter((emp) => emp.forced)
              .map((emp) => emp.id),
            ...Object.entries(notifyChecked)
              .filter(
                ([k, v]) =>
                  v &&
                  !notify_employees.find((e) => e.id === parseInt(k, 10))
                    ?.forced,
              )
              .map(([k]) => parseInt(k, 10)),
          ],
        }
        onSubmit(e, employeeData)
      }}
      className="max-w-4xl"
    >
      <div className="mb-6 grid gap-6 md:grid-cols-2">
        {/* Case Information */}
        <div className="border-hpi-light-gray rounded border p-4">
          <h2 className="mb-4 text-lg font-semibold">Case Information</h2>

          {isEdit && data.case_number && (
            <div className="mb-4">
              <label htmlFor="case_number" className="mb-1 block font-medium">
                Case Number
              </label>
              <input
                type="text"
                id="case_number"
                value={data.case_number}
                className="border-hpi-light-gray bg-hpi-pale-blue w-full rounded border px-3 py-2 text-gray-600"
                disabled
              />
            </div>
          )}

          {/* 1. HPI Case Name */}
          <div className="mb-4">
            <label htmlFor="hpi_name" className="mb-1 block font-medium">
              HPI Case Name <span className="text-hpi-red">*</span>
            </label>
            <input
              type="text"
              id="hpi_name"
              value={data.hpi_name}
              onChange={(e) => setData("hpi_name", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              required
            />
            {errors?.hpi_name && (
              <p className="text-hpi-red mt-1 text-sm">
                {errors.hpi_name.join(", ")}
              </p>
            )}
          </div>

          {/* 2. Customer (async search combobox) */}
          <div className="mb-4">
            <label htmlFor="customer_id" className="mb-1 block font-medium">
              Customer <span className="text-hpi-red">*</span>
            </label>
            <Combobox
              value={data.customer_id?.toString() ?? ""}
              onValueChange={(val) => {
                const numVal = val ? parseInt(val, 10) : null
                setData("customer_id", numVal)
              }}
              onInputValueChange={(value) => handleCustomerInputChange(value)}
              defaultInputValue={customer_display_name}
              filter={null}
              itemToStringLabel={(val) =>
                customerResults.find((c) => c.id.toString() === val)?.name ??
                val
              }
            >
              <ComboboxInput placeholder="Search customers..." showClear />
              <ComboboxContent>
                <ComboboxList>
                  <ComboboxEmpty>
                    {customerResults.length === 0
                      ? "Type 2+ characters to search..."
                      : "No customers found."}
                  </ComboboxEmpty>
                  {customerResults.map((c) => (
                    <ComboboxItem key={c.id} value={c.id.toString()}>
                      {c.name}
                    </ComboboxItem>
                  ))}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>
            {errors?.customer && (
              <p className="text-hpi-red mt-1 text-sm">
                {errors.customer.join(", ")}
              </p>
            )}
          </div>

          {/* 4. Intake By / Manager (row) */}
          <div className="mb-4 grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="intake_by_id" className="mb-1 block font-medium">
                Intake By
              </label>
              <select
                id="intake_by_id"
                value={data.intake_by_id ?? ""}
                onChange={(e) =>
                  setData(
                    "intake_by_id",
                    e.target.value ? parseInt(e.target.value) : null,
                  )
                }
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              >
                <option value="">Select...</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="manager_id" className="mb-1 block font-medium">
                Manager
              </label>
              <select
                id="manager_id"
                value={data.manager_id ?? ""}
                onChange={(e) =>
                  setData(
                    "manager_id",
                    e.target.value ? parseInt(e.target.value) : null,
                  )
                }
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              >
                <option value="">Select...</option>
                {managers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* 5. Status / After Hours (row) */}
          <div className="mb-4 grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="status" className="mb-1 block font-medium">
                Status <span className="text-hpi-red">*</span>
              </label>
              <select
                id="status"
                value={data.status}
                onChange={(e) => {
                  const newStatus = parseInt(e.target.value)
                  setData("status", newStatus)
                  // Clear closed_date when re-opening a closed case
                  if (data.status === 2 && newStatus !== 2) {
                    setData("closed_date", null)
                  }
                }}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                required
              >
                {status_options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={data.after_hours}
                  onChange={(e) => setData("after_hours", e.target.checked)}
                  className="rounded"
                />
                <span>After Hours</span>
              </label>
            </div>
          </div>

          {/* 6. Client Attorney / Attorney Phone (row) — always visible */}
          <div className="mb-4 grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="attorney_name" className="mb-1 block font-medium">
                Client Attorney
              </label>
              <input
                type="text"
                id="attorney_name"
                value={data.attorney_name}
                onChange={(e) => setData("attorney_name", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              />
            </div>
            <div>
              <label
                htmlFor="attorney_phone"
                className="mb-1 block font-medium"
              >
                Client Attorney Phone#
              </label>
              <input
                type="tel"
                id="attorney_phone"
                value={data.attorney_phone}
                onChange={(e) => setData("attorney_phone", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              />
            </div>
          </div>

          {/* 6b. Attorney Law Firm / Attorney Email (row) */}
          <div className="mb-4 grid gap-4 md:grid-cols-2">
            <div>
              <label
                htmlFor="attorney_law_firm"
                className="mb-1 block font-medium"
              >
                Attorney Law Firm
              </label>
              <input
                type="text"
                id="attorney_law_firm"
                value={data.attorney_law_firm}
                onChange={(e) => setData("attorney_law_firm", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              />
            </div>
            <div>
              <label
                htmlFor="attorney_email"
                className="mb-1 block font-medium"
              >
                Attorney Email
              </label>
              <input
                type="email"
                id="attorney_email"
                value={data.attorney_email}
                onChange={(e) => setData("attorney_email", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              />
            </div>
          </div>

          {/* 7. Contact at Customer / Location (conditional, row) */}
          {customerContacts.length > 0 && (
            <div className="mb-4 grid gap-4 md:grid-cols-2">
              <div>
                <label
                  htmlFor="customer_user_id"
                  className="mb-1 block font-medium"
                >
                  Contact at Customer
                </label>
                <select
                  id="customer_user_id"
                  value={data.customer_user_id ?? ""}
                  onChange={(e) =>
                    setData(
                      "customer_user_id",
                      e.target.value ? parseInt(e.target.value) : null,
                    )
                  }
                  className="border-hpi-light-gray w-full rounded border px-3 py-2"
                >
                  <option value="">Select...</option>
                  {customerContacts.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              {customerLocations.length > 0 && (
                <div>
                  <label
                    htmlFor="customer_location_id"
                    className="mb-1 block font-medium"
                  >
                    Location
                  </label>
                  <select
                    id="customer_location_id"
                    value={data.customer_location_id ?? ""}
                    onChange={(e) =>
                      setData(
                        "customer_location_id",
                        e.target.value ? parseInt(e.target.value) : null,
                      )
                    }
                    className="border-hpi-light-gray w-full rounded border px-3 py-2"
                  >
                    <option value="">Select...</option>
                    {customerLocations.map((loc) => (
                      <option key={loc.id} value={loc.id}>
                        {loc.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* 8. Case Type / Other Type (row) */}
          <div className="mb-4 grid gap-4 md:grid-cols-2">
            <div>
              <label
                htmlFor="investigation_type_id"
                className="mb-1 block font-medium"
              >
                Investigation Type <span className="text-hpi-red">*</span>
              </label>
              <select
                id="investigation_type_id"
                value={data.investigation_type_id ?? ""}
                onChange={(e) =>
                  setData(
                    "investigation_type_id",
                    e.target.value ? parseInt(e.target.value) : null,
                  )
                }
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                required
              >
                <option value="">Select...</option>
                {investigation_types.map((it) => (
                  <option key={it.id} value={it.id}>
                    {it.name}
                  </option>
                ))}
              </select>
              {errors?.investigation_type && (
                <p className="text-hpi-red mt-1 text-sm">
                  {errors.investigation_type.join(", ")}
                </p>
              )}
            </div>
            {selectedTypeName === "Other" && (
              <div>
                <label htmlFor="other_type" className="mb-1 block font-medium">
                  Other Type
                </label>
                <input
                  type="text"
                  id="other_type"
                  value={data.other_type}
                  onChange={(e) => setData("other_type", e.target.value)}
                  className="border-hpi-light-gray w-full rounded border px-3 py-2"
                  maxLength={50}
                />
              </div>
            )}
          </div>

          {/* 9. Budget / Retainer (row) */}
          <div className="mb-4 grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="budget" className="mb-1 block font-medium">
                Budget
              </label>
              <input
                type="number"
                step="0.01"
                id="budget"
                value={data.budget ?? ""}
                onChange={(e) =>
                  setData(
                    "budget",
                    e.target.value ? parseFloat(e.target.value) : null,
                  )
                }
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              />
            </div>
            <div>
              <label htmlFor="retainer" className="mb-1 block font-medium">
                Retainer
              </label>
              <input
                type="number"
                step="0.01"
                id="retainer"
                value={data.retainer ?? ""}
                onChange={(e) =>
                  setData(
                    "retainer",
                    e.target.value ? parseFloat(e.target.value) : null,
                  )
                }
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              />
            </div>
          </div>

          {/* 10. Client's Case/File# */}
          <div className="mb-4">
            <label htmlFor="case_reference" className="mb-1 block font-medium">
              Client&apos;s Case/File#
            </label>
            <input
              type="text"
              id="case_reference"
              value={data.case_reference}
              onChange={(e) => setData("case_reference", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
            />
          </div>

          {/* File Number — edit only */}
          {isEdit && (
            <div className="mb-4">
              <label htmlFor="file_number" className="mb-1 block font-medium">
                File Number
              </label>
              {data.status === 0 ? (
                <div className="flex items-center px-3 py-2">
                  <span className="bg-hpi-red rounded px-2 py-0.5 text-sm font-semibold text-white">
                    TBA
                  </span>
                </div>
              ) : (
                <input
                  type="text"
                  id="file_number"
                  value={data.file_number}
                  onChange={(e) => setData("file_number", e.target.value)}
                  className="border-hpi-light-gray w-full rounded border px-3 py-2"
                />
              )}
            </div>
          )}
        </div>

        {/* Dates — edit only, plus Intake Time for new */}
        <div className="border-hpi-light-gray rounded border p-4">
          {isEdit && (
            <>
              <h2 className="mb-4 text-lg font-semibold">Dates</h2>

              <div className="mb-4 grid gap-4 md:grid-cols-2">
                <div>
                  <label htmlFor="open_date" className="mb-1 block font-medium">
                    Open Date
                  </label>
                  <input
                    type="date"
                    id="open_date"
                    value={data.open_date ?? ""}
                    onChange={(e) =>
                      setData("open_date", e.target.value || null)
                    }
                    className="border-hpi-light-gray w-full rounded border px-3 py-2"
                  />
                </div>
                {data.status === 2 && (
                  <div>
                    <label
                      htmlFor="closed_date"
                      className="mb-1 block font-medium"
                    >
                      Closed Date
                    </label>
                    <input
                      type="date"
                      id="closed_date"
                      value={data.closed_date ?? ""}
                      onChange={(e) =>
                        setData("closed_date", e.target.value || null)
                      }
                      className="border-hpi-light-gray w-full rounded border px-3 py-2"
                    />
                  </div>
                )}
              </div>

              <div className="mb-4 grid gap-4 md:grid-cols-2">
                <div>
                  <label
                    htmlFor="accepted_date"
                    className="mb-1 block font-medium"
                  >
                    Accepted Date
                  </label>
                  <input
                    type="date"
                    id="accepted_date"
                    value={data.accepted_date ?? ""}
                    onChange={(e) =>
                      setData("accepted_date", e.target.value || null)
                    }
                    className="border-hpi-light-gray w-full rounded border px-3 py-2"
                  />
                </div>
                <div>
                  <label htmlFor="paid_date" className="mb-1 block font-medium">
                    Paid Date
                  </label>
                  <input
                    type="date"
                    id="paid_date"
                    value={data.paid_date ?? ""}
                    onChange={(e) =>
                      setData("paid_date", e.target.value || null)
                    }
                    className="border-hpi-light-gray w-full rounded border px-3 py-2"
                  />
                </div>
              </div>

              <h2 className="mt-6 mb-4 text-lg font-semibold">Location</h2>

              <div className="mb-4 grid gap-4 md:grid-cols-2">
                <div>
                  <label
                    htmlFor="location_city"
                    className="mb-1 block font-medium"
                  >
                    City
                  </label>
                  <input
                    type="text"
                    id="location_city"
                    value={data.location_city}
                    onChange={(e) => setData("location_city", e.target.value)}
                    className="border-hpi-light-gray w-full rounded border px-3 py-2"
                  />
                </div>
                <div>
                  <label
                    htmlFor="location_state"
                    className="mb-1 block font-medium"
                  >
                    State
                  </label>
                  <input
                    type="text"
                    id="location_state"
                    value={data.location_state}
                    onChange={(e) => setData("location_state", e.target.value)}
                    className="border-hpi-light-gray w-full rounded border px-3 py-2"
                    maxLength={2}
                  />
                </div>
              </div>
            </>
          )}

          {/* Notes section */}
          <h2 className={`mb-4 text-lg font-semibold ${isEdit ? "mt-6" : ""}`}>
            Notes
          </h2>

          <div className="mb-4">
            <label htmlFor="objectives" className="mb-1 block font-medium">
              Objectives <span className="text-hpi-red">*</span>
            </label>
            <textarea
              id="objectives"
              value={data.objectives}
              onChange={(e) => setData("objectives", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              rows={3}
              required
            />
            {errors?.objectives && (
              <p className="text-hpi-red mt-1 text-sm">
                {errors.objectives.join(", ")}
              </p>
            )}
          </div>

          <div className="mb-4">
            <label htmlFor="internal_notes" className="mb-1 block font-medium">
              Supervisor Notes
            </label>
            <textarea
              id="internal_notes"
              value={data.internal_notes}
              onChange={(e) => setData("internal_notes", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              rows={3}
            />
          </div>

          <div className="mb-4">
            <label htmlFor="notes" className="mb-1 block font-medium">
              General Notes
            </label>
            <textarea
              id="notes"
              value={data.notes}
              onChange={(e) => setData("notes", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              rows={3}
            />
          </div>

          {/* Intake Time — new only */}
          {!isEdit && (
            <>
              <h2 className="mt-6 mb-4 text-lg font-semibold">Intake Time</h2>
              <div className="mb-4 grid gap-4 md:grid-cols-2">
                <div>
                  <label
                    htmlFor="intake_hours"
                    className="mb-1 block font-medium"
                  >
                    Hours
                  </label>
                  <select
                    id="intake_hours"
                    name="intake_hours"
                    value={intakeHours}
                    onChange={(e) => setIntakeHours(parseInt(e.target.value))}
                    className="border-hpi-light-gray w-full rounded border px-3 py-2"
                  >
                    {Array.from({ length: 13 }, (_, i) => (
                      <option key={i} value={i}>
                        {i}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="intake_minutes"
                    className="mb-1 block font-medium"
                  >
                    Minutes
                  </label>
                  <select
                    id="intake_minutes"
                    name="intake_minutes"
                    value={intakeMinutes}
                    onChange={(e) => setIntakeMinutes(parseInt(e.target.value))}
                    className="border-hpi-light-gray w-full rounded border px-3 py-2"
                  >
                    {[0, 15, 30, 45].map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Referral Source */}
        <div className="border-hpi-light-gray rounded border p-4">
          <h2 className="mb-4 text-lg font-semibold">Referral</h2>
          <div className="mb-4">
            <label className="mb-1 block font-medium">Referred Credit to</label>
            <Combobox
              value={
                data.referral_customer_id ? `C${data.referral_customer_id}` : ""
              }
              onValueChange={(val) => {
                const strVal = val!
                if (!strVal) {
                  setData("referral_customer_id", null)
                  setData("referral_customer_user_id", null)
                  setReferralName("")
                  return
                }
                const result = referralResults.find((r) => r.id === strVal)
                if (result) {
                  if (result.type === "customer") {
                    setData("referral_customer_id", result.customer_id ?? null)
                    setData("referral_customer_user_id", null)
                  } else {
                    setData("referral_customer_id", null)
                    setData("referral_customer_user_id", null)
                  }
                  setReferralName(result.label)
                }
              }}
              onInputValueChange={(value) => handleReferralInputChange(value)}
              defaultInputValue={referralName}
              filter={null}
              itemToStringLabel={(val) =>
                referralResults.find((r) => r.id === val)?.label ?? val
              }
            >
              <ComboboxInput
                placeholder="Search referral source..."
                showClear
              />
              <ComboboxContent>
                <ComboboxList>
                  <ComboboxEmpty>
                    {referralResults.length === 0
                      ? "Type 3+ characters to search..."
                      : "No matching referrals."}
                  </ComboboxEmpty>
                  {referralResults.map((r) => (
                    <ComboboxItem key={r.id} value={r.id}>
                      {r.label}
                    </ComboboxItem>
                  ))}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>
          </div>
        </div>

        {/* Insurance Cases */}
        <div className="border-hpi-light-gray rounded border p-4">
          <h2 className="mb-4 text-lg font-semibold">Insurance Cases</h2>

          <div className="mb-4 grid gap-4 md:grid-cols-2">
            <div>
              <label
                htmlFor="insurance_days"
                className="mb-1 block font-medium"
              >
                # Days
              </label>
              <input
                type="number"
                id="insurance_days"
                value={data.insurance_days ?? ""}
                onChange={(e) =>
                  setData(
                    "insurance_days",
                    e.target.value ? parseInt(e.target.value) : null,
                  )
                }
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              />
            </div>
            <div>
              <label
                htmlFor="insurance_budget_notes"
                className="mb-1 block font-medium"
              >
                Budget Notes
              </label>
              <textarea
                id="insurance_budget_notes"
                value={data.insurance_budget_notes}
                onChange={(e) =>
                  setData("insurance_budget_notes", e.target.value)
                }
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                rows={2}
              />
            </div>
          </div>
        </div>

        {/* Final Report — edit only */}
        {isEdit && (
          <div className="border-hpi-light-gray rounded border p-4 md:col-span-2">
            <h2 className="mb-4 text-lg font-semibold">Final Report</h2>

            <div className="mb-4">
              <label htmlFor="report_name" className="mb-1 block font-medium">
                Report Case Name
              </label>
              <input
                type="text"
                id="report_name"
                value={data.report_name}
                onChange={(e) => setData("report_name", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={50}
              />
            </div>

            <div className="mb-4">
              <label htmlFor="prepared_for" className="mb-1 block font-medium">
                Prepared For
              </label>
              <textarea
                id="prepared_for"
                value={data.prepared_for}
                onChange={(e) => setData("prepared_for", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                rows={2}
              />
            </div>

            <div className="mb-4">
              <label htmlFor="matter" className="mb-1 block font-medium">
                In the Matter of
              </label>
              <textarea
                id="matter"
                value={data.matter}
                onChange={(e) => setData("matter", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                rows={2}
              />
            </div>
          </div>
        )}

        {/* Allow Access */}
        {allow_employees.length > 0 && (
          <div className="border-hpi-light-gray rounded border p-4">
            <h2 className="mb-4 text-lg font-semibold">Allow Access</h2>
            <p className="mb-2 text-xs text-gray-500">
              Limited access employees
            </p>
            <div className="max-h-48 space-y-2 overflow-y-auto">
              {allow_employees.map((emp) => (
                <label key={emp.id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    value={emp.id}
                    checked={allowChecked[emp.id] ?? false}
                    onChange={(e) =>
                      setAllowChecked((prev) => ({
                        ...prev,
                        [emp.id]: e.target.checked,
                      }))
                    }
                    className="rounded"
                  />
                  <span className="text-sm">{emp.name}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Deny Access */}
        {deny_employees.length > 0 && (
          <div className="border-hpi-light-gray rounded border p-4">
            <h2 className="mb-4 text-lg font-semibold">Deny Access</h2>
            <p className="mb-2 text-xs text-gray-500">Full access employees</p>
            <div className="max-h-48 space-y-2 overflow-y-auto">
              {deny_employees.map((emp) => (
                <label key={emp.id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    value={emp.id}
                    checked={denyChecked[emp.id] ?? false}
                    onChange={(e) =>
                      setDenyChecked((prev) => ({
                        ...prev,
                        [emp.id]: e.target.checked,
                      }))
                    }
                    className="rounded"
                  />
                  <span className="text-sm">{emp.name}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Notify */}
        {notify_employees.length > 0 && (
          <div className="border-hpi-light-gray rounded border p-4 md:col-span-2">
            <h2 className="mb-4 text-lg font-semibold">Notify</h2>
            <div className="grid max-h-48 gap-2 overflow-y-auto md:grid-cols-3">
              {notify_employees.map((emp) => (
                <label key={emp.id} className="flex items-center gap-2">
                  {emp.forced ? (
                    <>
                      <input type="hidden" value={emp.id} />
                      <span className="text-hpi-green text-sm">&#10003;</span>
                    </>
                  ) : (
                    <input
                      type="checkbox"
                      value={emp.id}
                      checked={notifyChecked[emp.id] ?? false}
                      onChange={(e) =>
                        setNotifyChecked((prev) => ({
                          ...prev,
                          [emp.id]: e.target.checked,
                        }))
                      }
                      className="rounded"
                    />
                  )}
                  <span className="text-sm">{emp.name}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={processing}
          className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
        >
          {submitLabel}
        </button>
        <Link
          href="/cases"
          className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
        >
          Cancel
        </Link>
      </div>
    </form>
  )
}
